import { grpcClientFactory } from '../../grpcClientFactory';
class DemUploadService {
    createAuthenticatedClient() {
        return grpcClientFactory.createAuthenticatedUserProjectsClient({ enableCompression: true });
    }
    async init(name, description) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to create projects');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.NewProjectFromDEMInitRequest;
                const grpcReq = new Req();
                grpcReq.setName(name);
                grpcReq.setDescription(description);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.newProjectFromDEMInit(grpcReq, metadata, (err, resp) => {
                    var _a, _b, _c, _d, _e;
                    if (err) {
                        reject(new Error(`Failed to init DEM project: ${err.code} ${err.message}`));
                        return;
                    }
                    resolve({
                        success: ((_a = resp.getSuccess) === null || _a === void 0 ? void 0 : _a.call(resp)) || false,
                        message: ((_b = resp.getMessage) === null || _b === void 0 ? void 0 : _b.call(resp)) || '',
                        projectId: ((_c = resp.getProjectId) === null || _c === void 0 ? void 0 : _c.call(resp)) || '',
                        uploadUrl: ((_d = resp.getUploadUrl) === null || _d === void 0 ? void 0 : _d.call(resp)) || '',
                        objectKey: ((_e = resp.getObjectKey) === null || _e === void 0 ? void 0 : _e.call(resp)) || ''
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to init DEM project: ${e}`));
            }
        });
    }
    async finalize(projectId, aoi) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to finalize DEM upload');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.FinalizeDEMUploadRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                // Optional area of interest within the uploaded DEM. Omitted = whole file.
                if (aoi) {
                    grpcReq.setRequestedBoundsGeojson(aoi.requestedBoundsGeoJSON);
                    grpcReq.setBufferBoundsGeojson(aoi.bufferBoundsGeoJSON);
                }
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.finalizeDEMUpload(grpcReq, metadata, (err, resp) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                    if (err) {
                        let errorMessage = `Failed to finalize DEM: ${err.code} ${err.message}`;
                        if (err.message && ((err.message.includes('duplicate key value violates unique constraint') &&
                            err.message.includes('user_projects_owner_id_requested_bounds_key')) ||
                            err.message.includes('SQLSTATE 23505'))) {
                            errorMessage = 'A project with these dimensions has already been created. Please upload a different DEM or check your existing projects.';
                        }
                        else if (err.message && (err.message.includes('unique constraint') ||
                            err.message.includes('SQLSTATE 23'))) {
                            errorMessage = 'A project with similar characteristics already exists. Please check your existing projects.';
                        }
                        reject(new Error(errorMessage));
                        return;
                    }
                    const validationErrors = ((_b = (_a = resp.getValidationErrorsList) === null || _a === void 0 ? void 0 : _a.call(resp)) === null || _b === void 0 ? void 0 : _b.map((ve) => {
                        var _a, _b, _c, _d, _e, _f;
                        return ({
                            type: ((_b = (_a = ve.getType) === null || _a === void 0 ? void 0 : _a.call(ve)) === null || _b === void 0 ? void 0 : _b.toString()) || '',
                            message: ((_c = ve.getMessage) === null || _c === void 0 ? void 0 : _c.call(ve)) || '',
                            userMessage: ((_d = ve.getUserMessage) === null || _d === void 0 ? void 0 : _d.call(ve)) || '',
                            details: ((_f = (_e = ve.getDetailsMap) === null || _e === void 0 ? void 0 : _e.call(ve)) === null || _f === void 0 ? void 0 : _f.toObject()) || {}
                        });
                    })) || [];
                    resolve({
                        success: ((_c = resp.getSuccess) === null || _c === void 0 ? void 0 : _c.call(resp)) || false,
                        message: ((_d = resp.getMessage) === null || _d === void 0 ? void 0 : _d.call(resp)) || '',
                        requestedBoundsGeojson: ((_e = resp.getRequestedBoundsGeojson) === null || _e === void 0 ? void 0 : _e.call(resp)) || '',
                        requestedAreaAcres: ((_f = resp.getRequestedAreaAcres) === null || _f === void 0 ? void 0 : _f.call(resp)) || 0,
                        tier: ((_g = resp.getTier) === null || _g === void 0 ? void 0 : _g.call(resp)) || 0,
                        totalPrice: ((_h = resp.getTotalPrice) === null || _h === void 0 ? void 0 : _h.call(resp)) || 0,
                        amountCents: ((_j = resp.getAmountCents) === null || _j === void 0 ? void 0 : _j.call(resp)) || 0,
                        validationErrors: validationErrors.length > 0 ? validationErrors : undefined,
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to finalize DEM: ${e}`));
            }
        });
    }
    // Starts the demo render for a finished DEM-upload project (flips it to
    // processing + kicks off the full-res render server-side).
    async startDemoProcessing(projectId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to start processing');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.StartDemoProcessingRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.startDemoProcessing(grpcReq, metadata, (err, resp) => {
                    var _a, _b, _c;
                    if (err) {
                        reject(new Error(`Failed to start processing: ${err.code} ${err.message}`));
                        return;
                    }
                    resolve({
                        success: ((_a = resp.getSuccess) === null || _a === void 0 ? void 0 : _a.call(resp)) || false,
                        message: ((_b = resp.getMessage) === null || _b === void 0 ? void 0 : _b.call(resp)) || '',
                        gateOutcome: ((_c = resp.getGateOutcome) === null || _c === void 0 ? void 0 : _c.call(resp)) || '',
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to start processing: ${e}`));
            }
        });
    }
    // Asks the workspace to build a full-access project at full resolution: a
    // preview-fidelity project (its full-res outputs were removed) or one that was
    // never built. Builds are queued one at a time per workspace, so the response
    // says whether this click queued a build or found one already under way.
    async requestFullResolutionBuild(projectId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to request a build');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.RequestFullResolutionBuildRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.requestFullResolutionBuild(grpcReq, metadata, (err, resp) => {
                    var _a, _b, _c;
                    if (err) {
                        reject(new Error(`Failed to request full resolution build: ${err.code} ${err.message}`));
                        return;
                    }
                    resolve({
                        success: ((_a = resp.getSuccess) === null || _a === void 0 ? void 0 : _a.call(resp)) || false,
                        message: ((_b = resp.getMessage) === null || _b === void 0 ? void 0 : _b.call(resp)) || '',
                        queued: ((_c = resp.getQueued) === null || _c === void 0 ? void 0 : _c.call(resp)) || false,
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to request full resolution build: ${e}`));
            }
        });
    }
    // The owner's free-demo volume-slot state: which projects hold slots, when
    // the next one frees, and the cap. Powers the cap-gate's wait-or-unlock
    // choice and the unpaid project panel's Start-demo affordance.
    async getDemoSlotInfo() {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to load demo slot info');
        }
        return new Promise((resolve, reject) => {
            try {
                const Empty = proto.google.protobuf.Empty;
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.getDemoSlotInfo(new Empty(), metadata, (err, resp) => {
                    var _a, _b, _c, _d;
                    if (err) {
                        reject(new Error(`Failed to load demo slot info: ${err.code} ${err.message}`));
                        return;
                    }
                    resolve({
                        slotsTotal: ((_a = resp.getSlotsTotal) === null || _a === void 0 ? void 0 : _a.call(resp)) || 0,
                        slotsUsed: ((_b = resp.getSlotsUsed) === null || _b === void 0 ? void 0 : _b.call(resp)) || 0,
                        nextSlotFreesAt: ((_c = resp.hasNextSlotFreesAt) === null || _c === void 0 ? void 0 : _c.call(resp)) ? resp.getNextSlotFreesAt() : undefined,
                        holders: (((_d = resp.getHoldersList) === null || _d === void 0 ? void 0 : _d.call(resp)) || []).map((h) => {
                            var _a, _b, _c, _d, _e;
                            return ({
                                projectId: ((_a = h.getProjectId) === null || _a === void 0 ? void 0 : _a.call(h)) || '',
                                name: ((_b = h.getName) === null || _b === void 0 ? void 0 : _b.call(h)) || '',
                                issuedAt: ((_c = h.getIssuedAt) === null || _c === void 0 ? void 0 : _c.call(h)) || 0,
                                slotFreesAt: ((_d = h.getSlotFreesAt) === null || _d === void 0 ? void 0 : _d.call(h)) || 0,
                                demoExpiresAt: ((_e = h.hasDemoExpiresAt) === null || _e === void 0 ? void 0 : _e.call(h)) ? h.getDemoExpiresAt() : undefined,
                            });
                        }),
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to load demo slot info: ${e}`));
            }
        });
    }
}
export const demUpload = new DemUploadService();
